import PaginatedQuestionPreview from "./PaginatedQuestionPreview";
import React from "react";
import styled from "styled-components";
import MarkdownIt from "markdown-it";
import MarkdownMathText from "../math/MarkdownMathText";
import { englishEmphasisSegments } from "../../shared/bilingualEnglishFormatting";

const hasChinese = (text) => /[\u3400-\u9fff\uf900-\ufaff]/.test(String(text || ""));
const contextText = (blocks) => (blocks || []).filter((block) => !["image", "figure"].includes(block.type))
  .map((block) => typeof block === "string" ? block : block.text || "").filter(Boolean).join("\n\n");
const optionsText = (options) => (options || []).map((option) => `(${option.label}) ${option.text || ""}`).join("\n");
const assetSource = (asset) => asset.url || (asset.data_base64 ? `data:${asset.content_type || "image/png"};base64,${asset.data_base64}` : "");
const markdown = new MarkdownIt({ html: false, breaks: true });
const getInlineImages = (source) => {
  const images = new Map();
  const collect = (tokens) => tokens.forEach((token) => {
    if (token.type === "image") images.set(token.attrGet("src"), { src: token.attrGet("src"), alt: token.content });
    if (token.children) collect(token.children);
  });
  collect(markdown.parse(source, {}));
  return [...images.values()];
};

const EnglishMarkdown = ({ source, emphasisSource = source, spans }) => <MarkdownMathText source={source} includeImages={false}
  formatText={(text) => englishEmphasisSegments(text, emphasisSource, spans).map((part, index) => {
    const content = part.underline ? <u>{part.text}</u> : part.text;
    return part.bold ? <strong key={index}>{content}</strong> : <React.Fragment key={index}>{content}</React.Fragment>;
  })} />;

export default function BilingualDocumentPreview({ document, title, range = "", mode = document.metadata?.mode }) {
  let number = 0;
  const chineseFirst = document.metadata?.bilingual_order === "chinese_first";
  const items = (document.sections || []).flatMap((section, sectionIndex) => (section.questions || []).map((question, questionIndex) => {
        const translation = question.bilingual || {};
        const presentation = translation.presentation || {};
        const sharedOptions = translation.shared_options ?? !(question.options || []).some((option) => hasChinese(option.text));
        const englishParts = [
          ...(translation.shared_context || []).map((block, index) => ["image", "figure"].includes(block.type) ? null
            : { source: typeof block === "string" ? block : block.text || "", spans: presentation.shared_context?.[index] }),
          { source: translation.stem || "", spans: presentation.stem },
          ...(!sharedOptions ? (translation.options || []).map((option) => ({
            source: `(${option.label}) ${option.text || ""}`, emphasisSource: option.text || "",
            spans: presentation.options?.find((item) => item.label === option.label)?.spans,
          })) : []),
        ].filter((part) => part?.source);
        const chinese = [contextText(question.shared_context), question.stem,
          !sharedOptions && optionsText(question.options)].filter(Boolean).join("\n\n");
        const assetIds = new Set([
          ...(question.asset_ids || []),
          ...(question.shared_context || []).flatMap((block) => block.asset_ids || []),
          ...(question.content_blocks || []).flatMap((block) => block.asset_ids || []),
        ]);
        const originalText = [chinese, sharedOptions && optionsText(question.options)].filter(Boolean).join("\n\n");
        const inlineImages = getInlineImages(originalText);
        const inlineImageSources = new Set(inlineImages.map((image) => image.src));
        const images = (document.assets || []).filter((asset) => assetIds.has(asset.id) && !inlineImageSources.has(assetSource(asset)));
        const english = <EnglishText $secondary={chineseFirst} aria-label="英文翻譯">{englishParts.map((part, index) => <EnglishMarkdown key={index} {...part} />)}</EnglishText>;
        const original = <ChineseText $secondary={!chineseFirst} aria-label="原題中文"><MarkdownMathText source={chinese} includeImages={false} /></ChineseText>;
        number += 1;
        const id = String(question.math_bank?.id || question.id || `${sectionIndex}-${questionIndex}`);
        return { id, content: <>
          {questionIndex === 0 && section.title && <h4>{section.title}</h4>}
          <Question aria-label={`雙語題目 ${number}`}>
          <strong>{question.number || number}.</strong>
          <div>
            {chineseFirst ? <>{original}{english}</> : <>{english}{original}</>}
            {sharedOptions && question.options?.length > 0 && <EnglishText aria-label="共用選項">
              <EnglishMarkdown source={optionsText(question.options)} />
            </EnglishText>}
            {inlineImages.map((image) => <img key={image.src} src={image.src} alt={image.alt || "題目附圖"}
              style={{ maxWidth: "100%", maxHeight: "240px" }} />)}
            {images.map((asset) => <img key={asset.id} alt={asset.description || "題目附圖"}
              src={assetSource(asset)}
              style={{ maxWidth: "100%", maxHeight: "240px" }} />)}
            {mode !== "student" && <ResponseRow>
              <div>{mode !== "student" && question.answer && <><strong>【答案】</strong><MarkdownMathText source={question.answer} /></>}</div>
              <div style={{ visibility: "hidden" }} aria-hidden="true"><div>{question.math_bank?.question_source || question.source_text || "\u00a0"}</div><div>{question.math_bank?.id || ""}</div></div>
            </ResponseRow>}
            {["teaching", "teaching_detailed"].includes(mode) && (question.solution || []).map((line, index) => <Solution key={index}><MarkdownMathText source={typeof line === "string" ? line : line.text || ""} /></Solution>)}
          </div>
        </Question></> };
      }));
  return <PreviewPaper><PaginatedQuestionPreview items={items} label="雙語考卷預覽"
    fontFamily={'"Times New Roman", "BiauKai", "DFKai-SB", serif'}
    header={<><h3>{title || document.title || "雙語考卷"}</h3>{range && <p>範圍：{range}</p>}</>} />
  </PreviewPaper>;
}

const PreviewPaper = styled.div`
  min-width: 0;
  h3 { text-align: center; margin: 0 0 1em; }
  h4 { margin: 1em 0 0.6em; }
`;
const ResponseRow = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 12px; color: red; margin: 12px 0; font-size: 12px; > div:last-child { text-align: right; overflow-wrap: anywhere; }`;
const Solution = styled.div`color: red; margin: 8px 0;`;
const Question = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.45em;
  margin: 0 0 1.4em;
  break-inside: avoid;
`;
const EnglishText = styled.div`
  color: ${({ $secondary }) => $secondary ? "#737a82" : "#171717"};
  border-left: ${({ $secondary }) => $secondary ? "2px solid #c3c8d0" : "none"};
  padding-left: ${({ $secondary }) => $secondary ? "0.75em" : "0"};
  margin: ${({ $secondary }) => $secondary ? "0.5em 0" : "0 0 0.5em"};
`;
const ChineseText = styled.div`
  color: ${({ $secondary }) => $secondary ? "#737a82" : "#000000"};
  font-family: "BiauKai", "DFKai-SB", "標楷體", "KaiTi", serif;
  border-left: ${({ $secondary }) => $secondary ? "2px solid #c3c8d0" : "none"};
  padding-left: ${({ $secondary }) => $secondary ? "0.75em" : "0"};
  margin: 0.5em 0;
  font-size: ${({ $secondary }) => $secondary ? "0.9em" : "1em"};
`;
